/**
 * Add the original terms (Isaiah Cotto, 2026-10-21): "Her terms did not carry over, so we need a way to add the original
 * terms in the event they are missing."
 *
 * Shown in the case's Terms section only while the case has NO terms in eMerge. The person types the terms DCF holds --
 * months, the monthly amount, the total, the first and last month -- and where they come from. The board's own sentence
 * is shown beside the form as a reference; nothing is read from it or filled in from it. The same checks run here and on
 * the server (months x monthly = total within a cent a month; the month span = the months), and the server decides.
 *
 * Recorded once (a retried click carries the same key). The packet is prepared again: a hold on the amount clears, and a
 * correction then asks for the terms it sends (Change terms).
 */
import React, { useRef, useState } from 'react';
import { umedApi } from './umedApi';
import type { OriginalTermsResult } from './umedApi';
import { TermsInputs } from '../finance/ResubmittalsKanban';
import { EMPTY_TERMS, dollarsToCents, dollars } from '../finance/resubmittalsModel';
import type { TermsFields } from '../finance/resubmittalsModel';

type Outcome = { success: boolean; error?: string; message?: string; code?: string; status?: number };
const said = (r: Outcome): string => {
  if (r.status === undefined) return 'Could not reach the server; the terms may or may not have been recorded. Read the case again before trying twice.';
  if (r.status >= 500) return 'The server failed. Read the case again before trying twice; it is not a permissions problem.';
  return r.error || r.message || (r.code ? `Refused (${r.code}).` : 'The server refused this.');
};
const freshKey = () => (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function' ? crypto.randomUUID() : `ot-${Date.now()}-${Math.random().toString(36).slice(2)}`);

/** The first-to-last month span, both months counted ("11/25-09/26" is 11). */
export function monthSpan(from: string, to: string): number {
  return (Number(to.slice(0, 4)) * 12 + Number(to.slice(5, 7))) - (Number(from.slice(0, 4)) * 12 + Number(from.slice(5, 7))) + 1;
}

export interface OriginalTermsBody { months: number; monthly_cents: number; total_cents: number; starts_on: string; ends_on: string; note: string }

/** The typed fields as the request, or the one thing wrong -- the server's own checks, said before anything is sent. */
export function originalTermsBody(f: TermsFields, source: string): { ok: true; body: OriginalTermsBody } | { ok: false; message: string } {
  const months = /^\d+$/.test(f.months.trim()) ? Number(f.months.trim()) : NaN;
  if (!Number.isInteger(months) || months < 1 || months > 60) return { ok: false, message: 'The term is a whole number of months, from 1 to 60.' };
  const monthly = dollarsToCents(f.monthly);
  if (monthly === null || monthly <= 0) return { ok: false, message: 'Enter the monthly amount in dollars and cents, more than nothing.' };
  const total = dollarsToCents(f.total);
  if (total === null || total <= 0) return { ok: false, message: 'Enter the total in dollars and cents, more than nothing.' };
  if (Math.abs(months * monthly - total) >= months) {
    return { ok: false, message: `${months} ${months === 1 ? 'month' : 'months'} at ${dollars(monthly)} is ${dollars(months * monthly)}, not ${dollars(total)}. The total must be the months times the monthly amount.` };
  }
  if (!/^\d{4}-\d{2}-\d{2}$/.test(f.startsOn)) return { ok: false, message: 'Enter the first month of the terms.' };
  if (!/^\d{4}-\d{2}-\d{2}$/.test(f.endsOn)) return { ok: false, message: 'Enter the last month of the terms.' };
  if (f.endsOn < f.startsOn) return { ok: false, message: 'The terms cannot end before they start.' };
  const span = monthSpan(f.startsOn, f.endsOn);
  if (span !== months) return { ok: false, message: `From the first month to the last is ${span} ${span === 1 ? 'month' : 'months'}, counting both, but the terms say ${months}. Check the dates or the months.` };
  const note = source.replace(/\s+/g, ' ').trim();
  if (note.length < 3 || note.length > 500) return { ok: false, message: 'Say where these terms come from: the DCF approval, the submitted packet, Finance.' };
  return { ok: true, body: { months, monthly_cents: monthly, total_cents: total, starts_on: f.startsOn, ends_on: f.endsOn, note } };
}

/** What the person is told once the terms are recorded: the line, then whether the packet is still held, in its own words. */
export function recordedMessage(r: OriginalTermsResult): string {
  const held = r.preparation && Array.isArray(r.preparation.blockers) ? r.preparation.blockers : [];
  const first = `Original terms recorded: ${r.termsText}.`;
  if (held.some((b) => b.code === 'CORRECTED_TERMS_MISSING')) return `${first} Now record the terms this correction sends with Change terms.`;
  if (held.length) return `${first} The packet is still held: ${held[0].message}`;
  return `${first} The packet is prepared again with them.`;
}

export default function OriginalTermsForm({ caseId, boardText, onDone, onCancel }: {
  caseId: string;
  /** The board's own terms sentence, shown as a reference only. */
  boardText: string | null;
  onDone: (msg: string) => void | Promise<void>;
  onCancel: () => void;
}) {
  const [fields, setFields] = useState<TermsFields>(EMPTY_TERMS);
  const [source, setSource] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  // One key per form: a retried click after a lost answer is the same record (the server replays it).
  const key = useRef(freshKey());
  const idp = `ot-${caseId}`;

  const save = async (e: React.FormEvent) => {
    e.preventDefault();
    const checked = originalTermsBody(fields, source);
    if (!checked.ok) { setErr(checked.message); return; }
    setBusy(true); setErr(null);
    try {
      const r = await umedApi.recordOriginalTerms(caseId, { ...checked.body, idempotency_key: key.current });
      if (!r.success || !r.data) { setErr(said(r)); return; }
      await onDone(recordedMessage(r.data));
    } catch {
      setErr('Could not reach the server; the terms may or may not have been recorded. Read the case again before trying twice.');
    } finally { setBusy(false); }
  };

  return (
    <form className="um-form" aria-label="Add the original terms" onSubmit={save} noValidate>
      <p className="um-quiet">The terms DCF holds for this UMED now, as they were submitted. Type them from the DCF approval, the submitted packet or Finance. Nothing is filled in for you.</p>
      {boardText && (
        <p className="um-quiet">Monday&rsquo;s terms, for reference only: <span className="um-text">{boardText}</span></p>
      )}
      <TermsInputs idp={idp} value={fields} onChange={(v) => { setFields(v); setErr(null); }}
        legend="Original terms" totalLabel="Total ($)" datesRequired withText={false} />
      <label htmlFor={`${idp}-source`}><span>Where these terms come from</span></label>
      <input id={`${idp}-source`} value={source} maxLength={500} disabled={busy} onChange={(e) => { setSource(e.target.value); setErr(null); }}
        placeholder="DCF approval letter, the submitted packet, Finance" />
      <p className="um-quiet">Recorded under your name as a new version; nothing is overwritten. The packet is prepared again with them. A correction still needs the terms it sends, recorded with Change terms.</p>
      <div className="btns">
        <button type="submit" className="um-open um-primary" disabled={busy}>{busy ? 'Recording…' : 'Record the original terms'}</button>
        <button type="button" className="um-link" disabled={busy} onClick={onCancel}>Cancel</button>
      </div>
      {err && <p className="um-notice stop" role="alert">{err}</p>}
    </form>
  );
}
